
import { Component, Prop, Vue } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { filterItemsWithAllergensAndDiets } from '@/utils/filters';
import { buildMonthGrid } from '@/utils/calendar';
import { addToCartExceedLimit, addToCartExceedMealsLimit } from '@/utils/helpers';
import CalendarDateCard from '@/components/menu/rotational-menus/CalendarDateCard.vue';
import MealSelectionPanel from '@/components/menu/rotational-menus/MealSelectionPanel.vue';
import MenuItemViewer from '@/components/menu/MenuItemViewer.vue';
import Modal from '@/components/shared/Modal.vue';
import WarningModal from '@/components/shared/WarningModal.vue';

const namespace: string = 'cart';

@Component<MonthlyCalendar>({
	components: {
		CalendarDateCard,
		MealSelectionPanel,
		MenuItemViewer,
		Modal,
		WarningModal
	}
})
export default class MonthlyCalendar extends Vue {
	@Prop({ type: Array, required: true }) private rotationalMenus!: RotationalMenu[];
	@Prop({ type: Boolean, required: false, default: false }) private orderPaused!: boolean;
	@Prop({ type: Array, required: false, default: () => [] }) private selectedDiets!: string[];
	@Prop({ type: Array, required: false, default: () => [] }) private selectedAllergens!: string[];
	@Action('setTemporaryItem', { namespace }) private setTemporaryItem!: (payload: { tempItem: MenuItem; quantity?: number }) => void;
	@Action('addToCart', { namespace }) private addToCart!: (extraData: any) => Promise<any>;
	@Action('setCosts', { namespace }) private setCosts!: (specifier?: string) => Promise<void>;
	@Action('removeFullItemFromCart', { namespace }) private removeFullItemFromCart!: (payload: { menuItem: OrderItem; orderDate?: string }) => Promise<void>;
	@Getter('getRotationalMenuOrders', { namespace }) private rotationalMenuOrders!: RotationalMenuOrder[];
	@Getter('isViewOnly', { namespace }) private isViewOnly!: boolean;
	@Getter('isAnonymousUser', { namespace: 'auth' }) private isAnonymousUser!: boolean;
	@Getter('getDateRange', { namespace: 'restaurant' }) private dateRange!: DateRange;
	@Getter('hasWeekendOrdering', { namespace: 'restaurant' }) private hasWeekendOrdering!: boolean;
	@Getter('orderingRequiresLogin', { namespace: 'restaurant' }) private orderingRequiresLogin!: boolean;
	@Getter('getMaxItemsPerCart', { namespace: 'restaurant' }) private maxItemsPerCart!: number | null;
	@Getter('getMaxMealsPerCart', { namespace: 'restaurant' }) private maxMealsPerCart!: number | null;

	private selectedDate: string | null = null;
	private currentDate: string = '';
	private panelOpen: boolean = false;
	private showDetails: boolean = false;
	private cartMutationInProgress: boolean = false;
	private showLoginModal: boolean = false;
	private currentMenuItemId: number | null = null;
	private currentItemSectionId: number | null | undefined = null;

	/**
	 * Flat, ordered list of days for the month grid (each week is exactly the
	 * column count, so a single CSS grid lays them out row by row).
	 *
	 * @return {CalendarDay[]}
	 */
	private get days(): CalendarDay[] {
		if (!this.dateRange.start) {
			return [];
		}
		return buildMonthGrid(this.dateRange.start, this.hasWeekendOrdering).flat();
	}

	/**
	 * Map each ISO date to its rotational menu, with items filtered by the
	 * currently selected diets and allergens.
	 *
	 * @return {{ [date: string]: RotationalMenu }}
	 */
	private get menusByDate(): { [date: string]: RotationalMenu } {
		const map: { [date: string]: RotationalMenu } = {};
		this.rotationalMenus.forEach((rotationalMenu: RotationalMenu) => {
			map[rotationalMenu.date] = this.filterRotationalMenu(rotationalMenu);
		});
		return map;
	}

	/**
	 * Map each ISO date to the set of selected meal item ids in the cart.
	 *
	 * @return {{ [date: string]: number[] }}
	 */
	private get selectedIdsByDate(): { [date: string]: number[] } {
		const map: { [date: string]: number[] } = {};
		this.rotationalMenuOrders.forEach((order: RotationalMenuOrder) => {
			map[order.date] = order.items.map((item: OrderItem) => item.id);
		});
		return map;
	}

	/**
	 * Flatten all meal items (menu + section items) for the modal's date.
	 *
	 * @return {MenuItem[]}
	 */
	private get modalMenuItems(): MenuItem[] {
		const rotationalMenu: RotationalMenu | null = this.menuForDate(this.currentDate);
		if (!rotationalMenu) {
			return [];
		}
		return rotationalMenu.menus.flatMap((menu: Menu) => [...menu.items, ...menu.sections.flatMap((section: MenuSection) => section.items)]);
	}

	/**
	 * Clean up the scroll-lock class if the calendar is destroyed while the item
	 * modal is open (e.g. toggling back to weekly or a route change).
	 *
	 * @return {void}
	 */
	private beforeDestroy(): void {
		document.documentElement.classList.remove('modal-open');
	}

	/**
	 * Get the (filtered) rotational menu for a date, or null if none.
	 *
	 * @param {string} date
	 * @return {RotationalMenu | null}
	 */
	private menuForDate(date: string): RotationalMenu | null {
		return this.menusByDate[date] || null;
	}

	/**
	 * Get the selected meal item ids for a date.
	 *
	 * @param {string} date
	 * @return {number[]}
	 */
	private selectedItemIdsForDate(date: string): number[] {
		return this.selectedIdsByDate[date] || [];
	}

	/**
	 * Filter a rotational menu's items (and section items) by diets/allergens.
	 *
	 * @param {RotationalMenu} rotationalMenu
	 * @return {RotationalMenu}
	 */
	private filterRotationalMenu(rotationalMenu: RotationalMenu): RotationalMenu {
		if (!this.selectedDiets.length && !this.selectedAllergens.length) {
			return rotationalMenu;
		}
		return {
			...rotationalMenu,
			menus: rotationalMenu.menus.map((menu: Menu) => ({
				...menu,
				items: filterItemsWithAllergensAndDiets(this.selectedDiets, this.selectedAllergens, menu.items),
				sections: menu.sections.map((section: MenuSection) => ({
					...section,
					items: filterItemsWithAllergensAndDiets(this.selectedDiets, this.selectedAllergens, section.items)
				}))
			}))
		};
	}

	/**
	 * Find every cart order item matching a meal for a given date (there can be
	 * more than one when the same meal was added with different options).
	 *
	 * @param {MenuItem} item
	 * @param {string} date
	 * @return {OrderItem[]}
	 */
	private findSelectedOrderItems(item: MenuItem, date: string): OrderItem[] {
		const order: RotationalMenuOrder | undefined = this.rotationalMenuOrders.find((o: RotationalMenuOrder) => o.date === date);
		return order?.items.filter((orderItem: OrderItem) => orderItem.id === item.id) || [];
	}

	/**
	 * Whether a meal has required option groups (forcing the details modal).
	 *
	 * @param {MenuItem} item
	 * @return {boolean}
	 */
	private hasRequiredOptions(item: MenuItem): boolean {
		return !!item.options?.some((group: OptionGroup) => group.required);
	}

	/**
	 * Whether a meal enforces a minimum quantity above one (forcing the details modal).
	 *
	 * @param {MenuItem} item
	 * @return {boolean}
	 */
	private hasMinimumQuantity(item: MenuItem): boolean {
		return (item.min_quantity ?? 0) > 1;
	}

	/**
	 * Toggle a meal chip: remove every instance if selected, open the modal if it
	 * has required options or a minimum quantity above one, otherwise add it to the
	 * cart directly at a quantity of 1.
	 *
	 * @param {MenuItem} item
	 * @param {string} date
	 * @return {Promise<void>}
	 */
	private async onToggle(item: MenuItem, date: string): Promise<void> {
		if (this.isViewOnly || this.orderPaused) {
			return;
		}

		// Silently drop clicks while a cart add/remove is still in flight. Spamming
		// otherwise fires overlapping requests that race on the shared order and its
		// temporary item, which the server rejects. No visual feedback is given by design.
		if (this.cartMutationInProgress) {
			return;
		}

		const existing: OrderItem[] = this.findSelectedOrderItems(item, date);

		if (!existing.length && this.isAnonymousUser && this.orderingRequiresLogin) {
			this.showLoginModal = true;
			return;
		}

		// A brand-new meal with required options or a minimum quantity above one opens
		// the modal instead request is made here, so it stays outside the
		// in-flight guard below.
		if (!existing.length && (this.hasRequiredOptions(item) || this.hasMinimumQuantity(item))) {
			this.onOpenDetails(item, date);
			return;
		}

		this.cartMutationInProgress = true;
		try {
			// Deselecting clears every instance of the meal for the day (a meal can be in the
			// cart more than once when added with different options), so one click fully toggles it off
			if (existing.length) {
				for (const orderItem of existing) {
					await this.removeFullItemFromCart({ menuItem: orderItem, orderDate: date });
				}
			}
			else {
				await this.addMealToCart(item, date);
			}
		}
		finally {
			this.cartMutationInProgress = false;
		}
	}

	/**
	 * Add a meal to the cart at a quantity of 1, guarding the cart limits.
	 *
	 * @param {MenuItem} item
	 * @param {string} date
	 * @return {Promise<void>}
	 */
	private async addMealToCart(item: MenuItem, date: string): Promise<void> {
		this.setTemporaryItem({ tempItem: item });

		// The limit helpers only return true when a (non-null) limit is configured
		if (addToCartExceedLimit()) {
			this.setTemporaryItem({ tempItem: {} as MenuItem });
			this.$toasted.show(this.$tc('menu.item_viewer.error_item_limit_exceeded', this.maxItemsPerCart!, { maxItemsPerCart: this.maxItemsPerCart! }), { type: 'error', position: 'top-center' }).goAway(5000);
			return;
		}

		if (addToCartExceedMealsLimit(date)) {
			this.setTemporaryItem({ tempItem: {} as MenuItem });
			this.$toasted.show(this.$tc('menu.item_viewer.error_quantity_meals_limit_exceeded', this.maxMealsPerCart!, { maxMealsPerCart: this.maxMealsPerCart! }), { type: 'error', position: 'top-center' }).goAway(5000);
			return;
		}

		try {
			await this.addToCart({ special_instructions: '', originalPrice: item.price, orderDate: date });
			await this.setCosts();
		}
		catch (error) {
			this.setTemporaryItem({ tempItem: {} as MenuItem });
			this.$toasted.show((error as any)?.response?.data?.message || this.$t('menu.item_viewer.error_adding_item_to_cart'), { type: 'error', position: 'top-center' }).goAway(5000);
		}
	}

	/**
	 * Open the item details modal for a meal on a given date.
	 *
	 * @param {MenuItem} item
	 * @param {string} date
	 * @return {void}
	 */
	private onOpenDetails(item: MenuItem, date: string): void {
		this.currentMenuItemId = item.id;
		this.currentItemSectionId = item.section_id;
		this.currentDate = date;
		this.showDetails = true;
		document.documentElement.classList.add('modal-open');
	}

	/**
	 * Hide the item details modal.
	 *
	 * @return {void}
	 */
	private hideMenuItemModal(): void {
		this.showDetails = false;
		this.currentMenuItemId = null;
		this.currentItemSectionId = null;
		document.documentElement.classList.remove('modal-open');
	}

	/**
	 * Send the anonymous user to the login page, preserving the current query/hash
	 * so they return to the same calendar state after signing in.
	 *
	 * @return {void}
	 */
	private redirectToLogin(): void {
		this.$router.push({ path: '/login', query: this.$route.query, hash: this.$route.hash }).catch(() => {});
	}

	/**
	 * Toggle the expand panel for a date: open it (or switch to it), or close it
	 * when it's already open for that same date.
	 *
	 * @param {string} date
	 * @return {void}
	 */
	private onExpand(date: string): void {
		if (this.panelOpen && this.selectedDate === date) {
			this.closePanel();
			return;
		}
		this.selectedDate = date;
		this.panelOpen = true;
	}

	/**
	 * Switch the open panel to another date (no effect when the panel is closed).
	 *
	 * @param {string} date
	 * @return {void}
	 */
	private onSelectDate(date: string): void {
		if (this.panelOpen) {
			this.selectedDate = date;
		}
	}

	/**
	 * Close the expand panel.
	 *
	 * @return {void}
	 */
	private closePanel(): void {
		this.panelOpen = false;
		this.selectedDate = null;
	}
}
